import { useEffect, useState } from "react";
import { api, fmtEur, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Tag, Plus, Trash, PencilSimple, Stack, Lightning, Translate, X, Minus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const blank = {
  name: "", price: 0, price_min: "", price_max: "", description: "",
  duration_min: 30, duration_days: "", sector: "", requires_inspection: false,
  requires_review: false,
  addons: [],
};

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplData, setTplData] = useState({ categories: [] });
  const [translating, setTranslating] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [durationMode, setDurationMode] = useState("min"); // 'min' | 'days'

  const load = async () => {
    const { data } = await api.get("/catalog");
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/catalog/sectors").then(({ data }) => setSectors(data.sectors || [])).catch(() => {});
  }, []);

  const ensureTpl = async () => {
    if (tplData.categories.length) return;
    try {
      const { data } = await api.get("/catalog/addon-templates");
      setTplData(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const startCreate = () => { setEditing(null); setForm(blank); setDurationMode("min"); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      name: it.name, price: it.price,
      price_min: it.price_min ?? "", price_max: it.price_max ?? "",
      description: it.description || "",
      duration_min: it.duration_min ?? 30,
      duration_days: it.duration_days ?? "",
      sector: it.sector || "",
      requires_inspection: !!it.requires_inspection,
      requires_review: !!it.requires_review,
      addons: (it.addons || []).map((a) => ({ ...a })),
    });
    setDurationMode(it.duration_days ? "days" : "min");
    setOpen(true);
  };

  const addAddonFromTpl = (tplAddon) => {
    // avoid duplicate by name
    if ((form.addons || []).some((a) => a.name === tplAddon.name)) {
      toast.info(`"${tplAddon.name}" ya está añadido`);
      return;
    }
    setForm({ ...form, addons: [...(form.addons || []), { ...tplAddon, id: crypto.randomUUID?.() }] });
    toast.success(`Añadido: ${tplAddon.name}`);
  };

  const addBlankAddon = () => {
    setForm({
      ...form,
      addons: [...(form.addons || []), {
        id: crypto.randomUUID?.(), name: "", price_per_unit: 10, max_qty: 5, duration_min_per_unit: 0, description: "",
      }],
    });
  };

  const updateAddon = (idx, key, val) => {
    const next = [...(form.addons || [])];
    next[idx] = { ...next[idx], [key]: val };
    setForm({ ...form, addons: next });
  };

  const removeAddon = (idx) => {
    setForm({ ...form, addons: (form.addons || []).filter((_, i) => i !== idx) });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        price: Number(form.price),
        price_min: form.price_min === "" ? null : Number(form.price_min),
        price_max: form.price_max === "" ? null : Number(form.price_max),
        description: form.description,
        duration_min: durationMode === "min" ? (Number(form.duration_min) || 30) : 30,
        duration_days: durationMode === "days" && form.duration_days ? Number(form.duration_days) : null,
        sector: form.sector || null,
        requires_inspection: !!form.requires_inspection,
        requires_review: !!form.requires_review,
        addons: (form.addons || []).map((a) => ({
          id: a.id, name: a.name, description: a.description || "",
          price_per_unit: Number(a.price_per_unit) || 0,
          max_qty: Number(a.max_qty) || 5,
          duration_min_per_unit: Math.max(0, Number(a.duration_min_per_unit) || 0),
          translations: a.translations || undefined,
        })),
      };
      if (editing) await api.put(`/catalog/${editing}`, payload);
      else await api.post("/catalog", payload);
      toast.success(editing ? "Item actualizado" : "Item creado");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inlinePrice = async (it, newPrice) => {
    const v = Number(newPrice);
    if (!Number.isFinite(v) || v === it.price) return;
    try {
      await api.put(`/catalog/${it.id}`, { price: v });
      toast.success(`Precio de "${it.name}" actualizado a ${fmtEur(v)}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const del = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.name}" del catálogo?`)) return;
    try { await api.delete(`/catalog/${it.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const translate = async (it) => {
    setTranslating(it.id);
    try {
      await api.post(`/catalog/${it.id}/translate`);
      toast.success(`"${it.name}" traducido a ES/CA/EN/PT con IA`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setTranslating(null); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Tipos y precios</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Catálogo de Servicios</h1>
          <p className="text-sm text-gray-600 mt-2">Cada servicio puede tener <b>complementos</b> con multiplicador (asientos × plaza, faros × unidad…). Usa la <b>biblioteca de plantillas</b> para añadirlos en un clic.</p>
        </div>
        <Button onClick={startCreate} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="catalog-new-btn">
          <Plus className="mr-1.5" /> Añadir servicio
        </Button>
      </header>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50">
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Nombre</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Complementos</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Duración</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Precio</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-10">Sin items en el catálogo</TableCell></TableRow>}
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`catalog-row-${it.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2"><Tag size={14} weight="duotone" className="text-[var(--ag-gold)]" /> {it.name}</div>
                  {it.description && <div className="text-xs text-gray-500 mt-0.5">{it.description}</div>}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {it.sector && (
                      <span className="text-[9px] uppercase tracking-wider bg-[#0077B6]/10 text-[var(--ag-gold)] px-1.5 py-0.5 rounded font-semibold" data-testid={`catalog-sector-${it.id}`}>
                        {(sectors.find(s => s.id === it.sector) || {}).label_es || it.sector}
                      </span>
                    )}
                    {it.requires_inspection && (
                      <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-semibold" data-testid={`catalog-inspection-${it.id}`}>
                        Requiere inspección
                      </span>
                    )}
                    {it.translations?.en && <span className="text-[9px] uppercase tracking-wider text-[#0077B6] flex items-center gap-1"><Translate size={9} /> ES·CA·EN·PT</span>}
                  </div>
                </TableCell>
                <TableCell>
                  {(it.addons || []).length === 0 ? (
                    <span className="text-xs text-[var(--ag-text-muted)]">—</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#0077B6]/10 text-[var(--ag-gold)] rounded font-semibold">
                      <Stack size={11} weight="fill" /> {(it.addons || []).length}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm text-gray-700">
                  {it.duration_days ? `${it.duration_days} día${it.duration_days > 1 ? "s" : ""}` : `${it.duration_min ?? 30} min`}
                </TableCell>
                <TableCell className="text-right">
                  <Input
                    type="number" step="0.01" defaultValue={it.price}
                    onBlur={(e)=>inlinePrice(it, e.target.value)}
                    className="h-8 w-24 text-right tabular-nums rounded-sm ml-auto"
                    data-testid={`catalog-price-${it.id}`}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={()=>translate(it)} disabled={translating === it.id} title="Traducir con IA" data-testid={`catalog-translate-${it.id}`}>
                    {translating === it.id
                      ? <span className="inline-block w-3.5 h-3.5 border-2 border-current border-r-transparent rounded-full animate-spin" />
                      : <Translate size={15} />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={()=>startEdit(it)} data-testid={`catalog-edit-${it.id}`}>
                    <PencilSimple size={15} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(it)} data-testid={`catalog-del-${it.id}`}>
                    <Trash size={15} className="text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tighter">{editing ? "Editar servicio" : "Nuevo servicio"}</DialogTitle>
            <DialogDescription className="text-xs">
              Define el servicio principal y, opcionalmente, sus complementos. Cada complemento se multiplica por la cantidad que elija el cliente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Service basic info */}
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre del servicio *</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="catalog-form-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción</Label>
              <Input value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">Precio</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e)=>setForm({...form, price: e.target.value})} className="mt-1 rounded-sm tabular-nums" data-testid="catalog-form-price" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Min</Label>
                <Input type="number" step="0.01" value={form.price_min} onChange={(e)=>setForm({...form, price_min: e.target.value})} className="mt-1 rounded-sm tabular-nums" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Max</Label>
                <Input type="number" step="0.01" value={form.price_max} onChange={(e)=>setForm({...form, price_max: e.target.value})} className="mt-1 rounded-sm tabular-nums" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Duración ({durationMode === "min" ? "min" : "días"})</Label>
                <div className="flex gap-1 mt-1">
                  <button type="button" onClick={()=>setDurationMode("min")}
                    className={`px-2 h-9 rounded-sm text-[10px] border ${durationMode==="min"?"bg-[var(--ag-gold)] text-white border-[var(--ag-gold)]":"bg-white text-gray-600 border-gray-200"}`}
                    data-testid="catalog-duration-mode-min">MIN</button>
                  <button type="button" onClick={()=>setDurationMode("days")}
                    className={`px-2 h-9 rounded-sm text-[10px] border ${durationMode==="days"?"bg-[var(--ag-gold)] text-white border-[var(--ag-gold)]":"bg-white text-gray-600 border-gray-200"}`}
                    data-testid="catalog-duration-mode-days">DÍAS</button>
                  {durationMode === "min" ? (
                    <Input type="number" step="15" value={form.duration_min}
                      onChange={(e)=>setForm({...form, duration_min: e.target.value})}
                      className="flex-1 rounded-sm tabular-nums" data-testid="catalog-form-duration" />
                  ) : (
                    <Input type="number" step="1" min="1" value={form.duration_days}
                      onChange={(e)=>setForm({...form, duration_days: e.target.value})}
                      placeholder="3"
                      className="flex-1 rounded-sm tabular-nums" data-testid="catalog-form-duration-days" />
                  )}
                </div>
              </div>
            </div>

            {/* Sector + inspección */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider">Subsector del ecosistema</Label>
                <select
                  value={form.sector}
                  onChange={(e)=>setForm({...form, sector: e.target.value})}
                  className="mt-1 w-full h-10 px-2 rounded-sm bg-white border border-gray-300 text-sm"
                  data-testid="catalog-form-sector"
                >
                  <option value="">— Sin clasificar —</option>
                  {sectors.map((s) => (
                    <option key={s.id} value={s.id}>{s.label_es}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer p-2 bg-amber-50 border border-amber-200 rounded-sm w-full" data-testid="catalog-form-inspection-label">
                  <input type="checkbox"
                    checked={form.requires_inspection}
                    onChange={(e)=>setForm({...form, requires_inspection: e.target.checked})}
                    className="accent-amber-600"
                    data-testid="catalog-form-inspection"
                  />
                  <span className="text-xs">"Precio desde…" · Requiere inspección presencial</span>
                </label>
              </div>
            </div>

            {/* Sprint E · #10 · Validación manual antes de confirmar */}
            <div>
              <label className="flex items-center gap-2 text-sm cursor-pointer p-3 bg-orange-50 border border-orange-200 rounded-sm" data-testid="catalog-form-requires-review-label">
                <input type="checkbox"
                  checked={form.requires_review}
                  onChange={(e)=>setForm({...form, requires_review: e.target.checked})}
                  className="accent-orange-600"
                  data-testid="catalog-form-requires-review"
                />
                <span className="text-xs">
                  <b className="text-orange-700">Requiere validación manual</b>
                  <span className="text-gray-600 block mt-0.5">Las reservas online de este servicio quedarán en estado "Pendiente de validación" hasta que aceptes o rechaces desde el panel.</span>
                </span>
              </label>
            </div>

            {/* Addons section */}
            <div className="border-t pt-4 border-gray-200">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
                    <Stack size={12} weight="fill" /> Complementos · multiplicador
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{(form.addons || []).length} configurado{(form.addons || []).length === 1 ? "" : "s"}</div>
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={async ()=>{ await ensureTpl(); setTplOpen(true); }} className="rounded-sm" data-testid="addon-tpl-open">
                    <Lightning size={13} weight="fill" className="mr-1 text-[#FFB703]" /> Plantillas
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={addBlankAddon} className="rounded-sm" data-testid="addon-add-blank">
                    <Plus size={13} className="mr-1" /> Vacío
                  </Button>
                </div>
              </div>

              {(form.addons || []).length === 0 ? (
                <div className="text-center bg-gray-50 border border-dashed border-gray-300 rounded-sm py-6 px-4 text-sm text-gray-500">
                  <Stack size={28} weight="duotone" className="mx-auto text-[var(--ag-text-dim)] mb-2" />
                  Sin complementos · Usa <b>Plantillas</b> para añadir los más comunes en un clic
                </div>
              ) : (
                <div className="space-y-2">
                  {(form.addons || []).map((a, idx) => (
                    <div key={a.id || idx} className="bg-gray-50 border border-gray-200 rounded-sm p-3 grid grid-cols-12 gap-2 items-center" data-testid={`addon-row-${idx}`}>
                      <Input value={a.name || ""} onChange={(e)=>updateAddon(idx, "name", e.target.value)}
                        placeholder="Nombre" className="col-span-4 h-9 rounded-sm" />
                      <Input type="number" step="0.5" value={a.price_per_unit ?? ""} onChange={(e)=>updateAddon(idx, "price_per_unit", e.target.value)}
                        placeholder="€/ud" className="col-span-2 h-9 rounded-sm tabular-nums text-right" data-testid={`addon-price-${idx}`} />
                      <div className="col-span-2 flex items-center gap-1">
                        <span className="text-[10px] text-gray-500 uppercase">Max</span>
                        <Input type="number" step="1" min="1" max="20" value={a.max_qty ?? 5} onChange={(e)=>updateAddon(idx, "max_qty", e.target.value)}
                          className="h-9 rounded-sm tabular-nums text-right" data-testid={`addon-max-${idx}`} />
                      </div>
                      <div className="col-span-3 flex items-center gap-1" title="Minutos extra que añade cada unidad al slot del servicio">
                        <span className="text-[10px] text-gray-500 uppercase">+min</span>
                        <Input type="number" step="5" min="0" max="480" value={a.duration_min_per_unit ?? 0}
                          onChange={(e)=>updateAddon(idx, "duration_min_per_unit", e.target.value)}
                          className="h-9 rounded-sm tabular-nums text-right" data-testid={`addon-duration-${idx}`} />
                      </div>
                      <Button type="button" size="icon" variant="ghost" onClick={()=>removeAddon(idx)} className="col-span-1 hover:bg-red-50 ml-auto" data-testid={`addon-del-${idx}`}>
                        <Trash size={13} className="text-red-500" />
                      </Button>
                      {a.description && <div className="col-span-12 text-[11px] text-gray-500 -mt-1 truncate">{a.description}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-[11px] text-gray-500">
              💡 Tras guardar, usa el botón <b>Traducir con IA</b> (icono <Translate size={10} className="inline mx-0.5" />) en la tabla para auto-traducir el servicio + complementos a ES/CA/EN/PT.
            </p>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submit} disabled={busy || !form.name} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="catalog-form-submit">{busy ? "Guardando…" : (editing ? "Actualizar" : "Crear")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates browser */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tpl-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tighter flex items-center gap-2">
              <Lightning size={20} weight="fill" className="text-[#FFB703]" /> Plantillas de complementos
            </DialogTitle>
            <DialogDescription className="text-xs">
              Selecciona complementos típicos por categoría. Se añadirán a tu servicio actual. Después podrás editar nombre, precio y máximo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {tplData.categories.map((cat) => (
              <div key={cat.id} data-testid={`tpl-cat-${cat.id}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold mb-2">{cat.label}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {cat.addons.map((a) => {
                    const already = (form.addons || []).some((x) => x.name === a.name);
                    return (
                      <button key={a.name} type="button" onClick={()=>addAddonFromTpl(a)} disabled={already}
                        className={`text-left border rounded-sm p-3 transition-all ${already ? "border-emerald-200 bg-emerald-50 cursor-default" : "border-gray-200 bg-white hover:border-[#FFB703] hover:shadow-sm active:scale-[0.99]"}`}
                        data-testid={`tpl-add-${a.name.replace(/\s+/g,'-')}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium text-sm text-[#1A1A1A] flex-1">{a.name}</div>
                          {already ? (
                            <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">✓ Añadido</span>
                          ) : (
                            <Plus size={12} weight="bold" className="text-[var(--ag-text-muted)] shrink-0 mt-1" />
                          )}
                        </div>
                        {a.description && <div className="text-[11px] text-gray-500 mt-1">{a.description}</div>}
                        <div className="text-[11px] text-[var(--ag-gold)] font-semibold mt-1.5 tabular-nums">
                          {fmtEur(a.price_per_unit)} / ud · max {a.max_qty}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {tplData.categories.length === 0 && <div className="text-center text-gray-500 py-8">Cargando plantillas…</div>}
          </div>
          <DialogFooter>
            <Button onClick={()=>setTplOpen(false)} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="tpl-done">Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
